/**
 * Persistent Media Cache Manager
 * Manages localStorage and IndexedDB synchronization for media caching
 * Provides persistence across browser sessions
 */

'use client';

import { getMediaTypeFromUrl } from './video-utils';

export interface CacheMetadata {
  url: string;
  type: 'video' | 'image' | 'unknown';
  timestamp: number;
  size?: number;
  cached: boolean;
}

export interface CacheState {
  version: string;
  metadata: CacheMetadata[];
  lastSync: number;
}

const CACHE_STATE_KEY = 'evoque-media-cache-state-v2';
const CACHE_VERSION = '2.0.0';
const INDEXEDDB_NAME = 'evoque-media-cache';
const INDEXEDDB_VERSION = 1;
const MAX_CACHE_SIZE = 500 * 1024 * 1024; // 500MB
const MAX_ITEM_SIZE = 5 * 1024 * 1024; // 5MB - items larger than this go to IndexedDB

class PersistentMediaCache {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize IndexedDB
   */
  async initialize(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    if (typeof window === 'undefined' || !('indexedDB' in window)) {
      return Promise.resolve();
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(INDEXEDDB_NAME, INDEXEDDB_VERSION);

      request.onerror = () => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[PersistentMediaCache] Failed to open IndexedDB:', request.error);
        }
        resolve(); // Resolve anyway - IndexedDB is optional
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store for media files
        if (!db.objectStoreNames.contains('media')) {
          const mediaStore = db.createObjectStore('media', { keyPath: 'url' });
          mediaStore.createIndex('timestamp', 'timestamp', { unique: false });
          mediaStore.createIndex('type', 'type', { unique: false });
        }

        // Create object store for cache metadata
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'url' });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Save cache state to localStorage
   */
  saveCacheState(metadata: CacheMetadata[]): void {
    if (typeof window === 'undefined' || !('localStorage' in window)) {
      return;
    }

    try {
      const state: CacheState = {
        version: CACHE_VERSION,
        metadata,
        lastSync: Date.now(),
      };

      // Compress state by only storing essential data
      const compressed = JSON.stringify(state);

      // Check size limit (localStorage has ~5-10MB limit)
      if (compressed.length > 4 * 1024 * 1024) {
        // If too large, only keep recent items (last 1000)
        const recent = metadata
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 1000);
        const limitedState: CacheState = {
          version: CACHE_VERSION,
          metadata: recent,
          lastSync: Date.now(),
        };
        localStorage.setItem(CACHE_STATE_KEY, JSON.stringify(limitedState));
      } else {
        localStorage.setItem(CACHE_STATE_KEY, compressed);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[PersistentMediaCache] Failed to save cache state:', error);
      }
    }
  }

  /**
   * Load cache state from localStorage
   */
  loadCacheState(): CacheMetadata[] {
    if (typeof window === 'undefined' || !('localStorage' in window)) {
      return [];
    }

    try {
      const stored = localStorage.getItem(CACHE_STATE_KEY);
      if (!stored) {
        return [];
      }

      const state: CacheState = JSON.parse(stored);

      // Validate version
      if (state.version !== CACHE_VERSION) {
        // Version mismatch - clear old cache
        localStorage.removeItem(CACHE_STATE_KEY);
        return [];
      }

      return state.metadata || [];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[PersistentMediaCache] Failed to load cache state:', error);
      }
      return [];
    }
  }

  /**
   * Store large media file in IndexedDB
   */
  async storeMedia(url: string, blob: Blob): Promise<boolean> {
    await this.initialize();
    if (!this.db) {
      return false;
    }

    try {
      const transaction = this.db.transaction(['media'], 'readwrite');
      const store = transaction.objectStore('media');

      const mediaType: 'video' | 'image' | 'unknown' = getMediaTypeFromUrl(url) || 'unknown';
      
      const mediaData: {
        url: string;
        blob: Blob;
        timestamp: number;
        size: number;
        type: 'video' | 'image' | 'unknown';
      } = {
        url,
        blob,
        timestamp: Date.now(),
        size: blob.size,
        type: mediaType,
      };

      await new Promise<void>((resolve, reject) => {
        const request = store.put(mediaData);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      // Update metadata
      await this.updateMetadata(url, {
        url,
        type: mediaData.type,
        timestamp: Date.now(),
        size: blob.size,
        cached: true,
      });

      // Check cache size and evict if needed
      await this.evictIfNeeded();

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[PersistentMediaCache] Failed to store media in IndexedDB:', error);
      }
      return false;
    }
  }

  /**
   * Retrieve large media file from IndexedDB
   */
  async getMedia(url: string): Promise<Blob | null> {
    await this.initialize();
    if (!this.db) {
      return null;
    }

    try {
      const transaction = this.db.transaction(['media'], 'readonly');
      const store = transaction.objectStore('media');

      const mediaData = await new Promise<any>((resolve, reject) => {
        const request = store.get(url);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (mediaData && mediaData.blob) {
        // Update access timestamp
        await this.updateMetadata(url, {
          ...mediaData,
          timestamp: Date.now(),
        });
        return mediaData.blob;
      }

      return null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[PersistentMediaCache] Failed to get media from IndexedDB:', error);
      }
      return null;
    }
  }

  /**
   * Check if media is stored in IndexedDB
   */
  async hasMedia(url: string): Promise<boolean> {
    await this.initialize();
    if (!this.db) {
      return false;
    }

    try {
      const transaction = this.db.transaction(['media'], 'readonly');
      const store = transaction.objectStore('media');

      const exists = await new Promise<boolean>((resolve, reject) => {
        const request = store.count(IDBKeyRange.only(url));
        request.onsuccess = () => resolve(request.result > 0);
        request.onerror = () => reject(request.error);
      });

      return exists;
    } catch (error) {
      return false;
    }
  }

  /**
   * Update metadata for a media item
   */
  private async updateMetadata(url: string, metadata: CacheMetadata): Promise<void> {
    await this.initialize();
    if (!this.db) {
      return;
    }

    try {
      const transaction = this.db.transaction(['metadata'], 'readwrite');
      const store = transaction.objectStore('metadata');
      await new Promise<void>((resolve, reject) => {
        const request = store.put(metadata);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Evict old entries if cache size exceeds limit (LRU)
   */
  private async evictIfNeeded(): Promise<void> {
    await this.initialize();
    if (!this.db) {
      return;
    }

    try {
      const transaction = this.db.transaction(['media', 'metadata'], 'readwrite');
      const mediaStore = transaction.objectStore('media');
      const metadataStore = transaction.objectStore('metadata');

      // Get all entries sorted by timestamp (oldest first)
      const allEntries = await new Promise<any[]>((resolve, reject) => {
        const request = mediaStore.index('timestamp').getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });

      // Calculate total size
      let totalSize = 0;
      for (const entry of allEntries) {
        totalSize += entry.size || 0;
      }

      // If over limit, evict oldest entries
      if (totalSize > MAX_CACHE_SIZE) {
        const sorted = allEntries.sort((a, b) => a.timestamp - b.timestamp);
        let sizeToRemove = totalSize - MAX_CACHE_SIZE;

        for (const entry of sorted) {
          if (sizeToRemove <= 0) break;

          await new Promise<void>((resolve, reject) => {
            const deleteRequest = mediaStore.delete(entry.url);
            deleteRequest.onsuccess = () => {
              metadataStore.delete(entry.url);
              resolve();
            };
            deleteRequest.onerror = () => reject(deleteRequest.error);
          });

          sizeToRemove -= entry.size || 0;
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[PersistentMediaCache] Failed to evict cache:', error);
      }
    }
  }

  /**
   * Clear all cached media
   */
  async clearCache(): Promise<void> {
    // Clear localStorage
    if (typeof window !== 'undefined' && 'localStorage' in window) {
      localStorage.removeItem(CACHE_STATE_KEY);
    }

    // Clear IndexedDB
    await this.initialize();
    if (this.db) {
      try {
        const transaction = this.db.transaction(['media', 'metadata'], 'readwrite');
        await Promise.all([
          new Promise<void>((resolve, reject) => {
            const request = transaction.objectStore('media').clear();
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
          }),
          new Promise<void>((resolve, reject) => {
            const request = transaction.objectStore('metadata').clear();
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
          }),
        ]);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[PersistentMediaCache] Failed to clear cache:', error);
        }
      }
    }
  }

  /**
   * Invalidate a specific URL from persistent cache
   */
  async invalidateUrl(url: string): Promise<void> {
    // Remove from localStorage state
    const metadata = this.loadCacheState();
    const filtered = metadata.filter((m) => m.url !== url);
    this.saveCacheState(filtered);

    // Remove from IndexedDB
    await this.initialize();
    if (this.db) {
      try {
        const transaction = this.db.transaction(['media', 'metadata'], 'readwrite');
        await Promise.all([
          new Promise<void>((resolve, reject) => {
            const request = transaction.objectStore('media').delete(url);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
          }),
          new Promise<void>((resolve, reject) => {
            const request = transaction.objectStore('metadata').delete(url);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
          }),
        ]);
      } catch (error) {
        // Silently fail
      }
    }
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{
    localStorageCount: number;
    indexedDBCount: number;
    indexedDBSize: number;
  }> {
    const metadata = this.loadCacheState();
    let indexedDBCount = 0;
    let indexedDBSize = 0;

    await this.initialize();
    if (this.db) {
      try {
        const transaction = this.db.transaction(['media'], 'readonly');
        const store = transaction.objectStore('media');
        const allEntries = await new Promise<any[]>((resolve, reject) => {
          const request = store.getAll();
          request.onsuccess = () => resolve(request.result || []);
          request.onerror = () => reject(request.error);
        });

        indexedDBCount = allEntries.length;
        indexedDBSize = allEntries.reduce((sum, entry) => sum + (entry.size || 0), 0);
      } catch (error) {
        // Silently fail
      }
    }

    return {
      localStorageCount: metadata.length,
      indexedDBCount,
      indexedDBSize,
    };
  }

  /**
   * Determine if a file should be stored in IndexedDB
   */
  shouldUseIndexedDB(size: number, url: string): boolean {
    const mediaType = getMediaTypeFromUrl(url);
    // Store videos and large images (>1MB) in IndexedDB
    return size > MAX_ITEM_SIZE || mediaType === 'video';
  }
}

// Export singleton instance
export const persistentMediaCache = new PersistentMediaCache();
